import React, { useContext, useMemo, useState } from 'react';

import PropTypes from 'prop-types';

import { variantSwitch } from '@adsk/alloy-react-helpers';
import theme, {
  stylePropType,
  StylableComponent,
  StyleProp,
} from '@adsk/alloy-react-theme';

import AvatarGroupContext from './AvatarGroupContext';
import {
  AVATAR_COLORS,
  AVATAR_SIZES,
  AvatarColor,
  AvatarSize,
  DEFAULT_AVATAR_NAME,
  DEFAULT_AVATAR_SUFFIX,
  REMOVED_USER_SUFFIX,
} from './constants';
import getColorByName from './getColorByName';
import initialsFromName from './initialsFromName';

const defaultVariants = {
  [AVATAR_SIZES.SMALL_16]: {
    width: '16px',
    height: '16px',
    fontSize: '11px',
  },
  [AVATAR_SIZES.MEDIUM_24]: {
    width: '24px',
    height: '24px',
    fontSize: '12px',
  },
  [AVATAR_SIZES.MEDIUM_32]: {
    width: '32px',
    height: '32px',
    fontSize: '16px',
  },
  [AVATAR_SIZES.LARGE_48]: {
    width: '48px',
    height: '48px',
    fontSize: '24px',
  },
  [AVATAR_SIZES.XLARGE_64]: {
    width: '64px',
    height: '64px',
    fontSize: '32px',
  },
  [AVATAR_SIZES.XXLARGE_128]: {
    width: '128px',
    height: '128px',
    fontSize: '56px',
  },
};

const groupVariants = {
  [AVATAR_SIZES.SMALL_16]: {
    marginLeft: '-3px',
    border: `1px solid ${theme.tokens.colors.border.inverse.value}`,
  },
  [AVATAR_SIZES.MEDIUM_24]: {
    marginLeft: '-6px',
    border: `2px solid ${theme.tokens.colors.border.inverse.value}`,
  },
  [AVATAR_SIZES.MEDIUM_32]: {
    marginLeft: '-6px',
    border: `2px solid ${theme.tokens.colors.border.inverse.value}`,
  },
  [AVATAR_SIZES.LARGE_48]: {
    marginLeft: '-7px',
    border: `2px solid ${theme.tokens.colors.border.inverse.value}`,
  },
  [AVATAR_SIZES.XLARGE_64]: {
    marginLeft: '-10px',
    border: `4px solid ${theme.tokens.colors.border.inverse.value}`,
  },
  [AVATAR_SIZES.XXLARGE_128]: {
    marginLeft: '-20px',
    border: `4px solid ${theme.tokens.colors.border.inverse.value}`,
  },
};

/**
 * Avatars are a visual representation of a customers identity.
 * These may be small thumbnails as part of a menu or more prominent, standalone elements on account and management views.
 * Initials are dynamically generated from the provided name and always rendered. This allows the initials to serve as a placeholder while the image is loading. Read more about when and how to use the Avatar component on the internal wiki.
 */
const Avatar = React.forwardRef<
  HTMLSpanElement,
  StylableComponent<HTMLSpanElement> & {
    name?: string;
    image?: string;
    imageUrl?: string | null;
    size?: AvatarSize;
    color?: AvatarColor;
  }
>(
  (
    {
      style,
      className,
      children,
      name,
      image,
      imageUrl,
      color: colorProp,
      size: sizeProp,
      ...props
    },
    ref,
  ) => {
    const [error, setError] = useState(false); // Whether image failed to load
    const [loaded, setLoaded] = useState(false); // Whether image loaded successfully

    let finalImage = imageUrl || image; // Support both imageUrl and image same as we use in our api
    if (finalImage && DEFAULT_AVATAR_SUFFIX.test(finalImage)) {
      finalImage = undefined; // remove default Image and show letter avatar instead
    }

    const finalName = useMemo(() => {
      if (name && REMOVED_USER_SUFFIX.test(name)) {
        return name.replace(REMOVED_USER_SUFFIX, ''); // remove any suffix inside parenthesis at the end
      }
      return name;
    }, [name]);

    const color = colorProp || getColorByName(finalName);

    const avatarGroup = useContext(AvatarGroupContext);
    const size = avatarGroup ? avatarGroup.size : sizeProp;
    const nameInitials = useMemo(
      () => initialsFromName(finalName, size),
      [finalName, size],
    );

    let sizeStyles: Partial<StyleProp> = {
      ...defaultVariants[size || AVATAR_SIZES.MEDIUM_32],
    };

    if (avatarGroup) {
      sizeStyles = {
        ...sizeStyles,
        ...groupVariants[size || AVATAR_SIZES.MEDIUM_32],
      };
    }

    return (
      <span
        {...props}
        className={className}
        ref={ref}
        role="img"
        data-testid="Avatar"
        css={[
          {
            display: 'inline-flex',
            rounded: 'full',
            alignItems: 'center',
            flexShrink: 0,
            justifyContent: 'center',
            position: 'relative',
            borderRadius: theme.borderRadius.circle,
            verticalAlign: 'middle',
            cursor: 'default',
            color: theme.tokens.colors.text.primary.value,
          },

          variantSwitch({
            statement: color,
            variants: {
              [AVATAR_COLORS.COLOR_1]: {
                background:
                  theme.tokens.colors.background.accent.one.subtle.value,
              },
              [AVATAR_COLORS.COLOR_2]: {
                background: theme.tokens.colors.background.accent.two.value,
              },
              [AVATAR_COLORS.COLOR_3]: {
                background:
                  theme.tokens.colors.background.accent.two.subtle.value,
              },
              [AVATAR_COLORS.COLOR_4]: {
                background:
                  theme.tokens.colors.background.accent.four.subtle.value,
              },
              [AVATAR_COLORS.COLOR_5]: {
                background:
                  theme.tokens.colors.background.accent.three.subtle.value,
              },
              [AVATAR_COLORS.COLOR_6]: {
                background:
                  theme.tokens.colors.background.accent.five.subtler.value,
              },
              [AVATAR_COLORS.COLOR_7]: {
                background:
                  theme.tokens.colors.background.accent.five.subtle.value,
              },
            },
          }),

          sizeStyles,
          avatarGroup?.size && { boxSizing: 'content-box' },
          style,
        ]}
      >
        {!error && finalImage && (
          <img
            css={[
              {
                width: '100%',
                height: '100%',
                borderRadius: theme.borderRadius.circle,
                objectFit: 'cover',
                ...(!loaded && { display: 'none' }), // hide image until loaded
              },
            ]}
            alt={finalName}
            src={finalImage}
            data-testid="Avatar-img"
            onLoad={() => setLoaded(true)}
            onError={() => setError(true)}
          />
        )}
        {(error || !loaded || !finalImage) &&
          (children || (nameInitials && <span>{nameInitials}</span>))}
      </span>
    );
  },
);

Avatar.displayName = 'Avatar';

Avatar.defaultProps = {
  name: DEFAULT_AVATAR_NAME,
  size: AVATAR_SIZES.MEDIUM_32,
};

Avatar.propTypes = {
  /** Avatar's content when image is missing */
  children: PropTypes.any,
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The user full name for the avatar */
  name: PropTypes.string,
  /** Set the size of the avatar */
  size: PropTypes.oneOf(Object.values(AVATAR_SIZES)),
  /** Set the colors of the avatar */
  color: PropTypes.oneOf(Object.values(AVATAR_COLORS)),
  /** URL to a profile image */
  image: PropTypes.string,
  /** URL to a profile image */
  imageUrl: PropTypes.string,
};

export default Object.assign(Avatar, {
  SIZES: AVATAR_SIZES,
  COLORS: AVATAR_COLORS,
});
